import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, Loader2, Save, Send } from "lucide-react";

type Settings = {
  enabled: boolean;
  window_minutes: number;
  rate_limited_threshold: number;
  ticket_rate_limited_threshold: number;
  error_threshold: number;
  denied_threshold: number;
  notify_email: string | null;
  slack_webhook_url: string | null;
  cooldown_minutes: number;
};

const FIELDS: Array<{ k: keyof Settings; label: string; hint?: string }> = [
  { k: "window_minutes", label: "Window (minutes)" },
  { k: "cooldown_minutes", label: "Cooldown (minutes)" },
  { k: "rate_limited_threshold", label: "Rate-limit spike ≥" },
  { k: "ticket_rate_limited_threshold", label: "Ticket rate-limit ≥", hint: "create_ticket only" },
  { k: "error_threshold", label: "Error spike ≥" },
  { k: "denied_threshold", label: "Denied spike ≥" },
];

export function HelpdeskAlertSettingsPanel() {
  const [s, setS] = useState<Settings | null>(null);
  const [busy, setBusy] = useState<null | "save" | "test">(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_settings" as any)
      .select("enabled,window_minutes,rate_limited_threshold,ticket_rate_limited_threshold,error_threshold,denied_threshold,notify_email,slack_webhook_url,cooldown_minutes")
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    setS(data as unknown as Settings);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!s) return;
    setBusy("save");
    const { error } = await supabase.from("helpdesk_alert_settings" as any).update(s).eq("id", true);
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Alert settings saved");
  };

  const test = async () => {
    setBusy("test");
    const { data, error } = await supabase.functions.invoke("helpdesk-alerts-monitor", {
      body: {},
      // dry_run via query string is easier; but invoke doesn't support it directly.
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Monitor ran", { description: JSON.stringify((data as any)?.counts ?? data) });
  };

  if (loading || !s) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading alert settings…</div>;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Bell className="size-4" /> Helpdesk alerts
          </h3>
          <p className="text-sm text-muted-foreground">
            Emails and/or Slack when helpdesk-chat rate limits, errors, or denials spike.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />
          <Label>Enabled</Label>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {FIELDS.map(({ k, label, hint }) => (
          <div key={k as string} className="space-y-1.5">
            <Label>{label}</Label>
            <Input
              type="number"
              min={1}
              value={s[k] as number}
              onChange={(e) => setS({ ...s, [k]: Math.max(1, Number(e.target.value) || 1) })}
            />
            {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Notify email</Label>
          <Input type="email" placeholder="ops@yourdomain.com"
            value={s.notify_email ?? ""}
            onChange={(e) => setS({ ...s, notify_email: e.target.value || null })} />
        </div>
        <div className="space-y-1.5">
          <Label>Slack webhook URL</Label>
          <Input type="url" placeholder="https://hooks.slack.com/services/…"
            value={s.slack_webhook_url ?? ""}
            onChange={(e) => setS({ ...s, slack_webhook_url: e.target.value || null })} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={test} disabled={!!busy}>
          {busy === "test" ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          Run monitor now
        </Button>
        <Button onClick={save} disabled={!!busy}>
          {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}
